import { useState, createContext, useContext } from 'react';
import styled from '@emotion/styled';
import axios from '../../axios';

import { useTheme, Tab, Tabs, Breadcrumbs, Grid, Card, Avatar, Badge } from '@mui/material';

import { Container, StyledLink, BasicFlex } from '../../utils/Common';

import { useLocation, useParams } from 'react-router-dom';

import PhotoCameraFrontRoundedIcon from '@mui/icons-material/PhotoCameraFrontRounded';

import { useQuery } from '@tanstack/react-query';

import LoadingPageComponent from './LoadingPageComponent';

import { PageDataContext } from '../../contexts/PageData';

import useIsMobile from '../../hooks/useIsMobile';

const ProfileBlock = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${props => props.theme.colors.darkBg};
  border-radius: .8rem;
  margin-bottom: 4.5rem;
`

const StyledAvatar = styled(Avatar)`
  position: relative;
  top: 4.5rem;
  border: 5px solid ${props => props.theme.colors.background.paper};
  box-sizing: border-box;
  width: 10rem;
  height: 10rem;
`;

const TabContentStyles = styled.div`
  margin-top: 2rem;
`;

const StyledTab = styled(Tab)`
  gap: .5rem;
  padding: 1rem 3rem;
  min-height: auto;

  @media ${props => props.theme.breaks.down("md")} { 
    gap: .2rem;
    padding: .3rem 1rem;
    font-size: 1.2rem;
  }
`

const StyledTabLabel = styled(BasicFlex)`
  gap: .5rem;
  width: auto;
`

const TabLabel = ({label, badge}) => {
  return (
    <StyledTabLabel>
      <span>{label}</span>
      {badge && <Badge badgeContent={badge} color="secondary" sx={{ position: "relative", top: "-.1rem", left: ".8rem" }} />}
    </StyledTabLabel>
  );
}

const TabContent = ({value, panel, children}) => {
  if (value !== panel) return <div></div>
  return <TabContentStyles>{children}</TabContentStyles>;
}
const ViewDetails = ({ 
  title, 
  identifier = "details", 
  tabs, 
  tabContent, 
  children, 
  isProfile = false, 
  queryOptions = {}, 
  url,
  show_breadcrumbs = true 
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const location = useLocation();
  const { pathname } = location;
  const baseUrl = pathname.split('/view')[0];
  const { id } = useParams();

  const theme = useTheme();

  const { isMobile } = useIsMobile();


  const handleChangeTab = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getInfo = async () => {
    try {
      const { data } = await axios.get(`/${url}/${id}?full=true`);
      data.backend_data_loaded = true;
      return data;
    } catch (e) {
      return null;
    }
  }

  const queryResult = useQuery({
    queryKey: [url, id],
    queryFn: getInfo,
    ...queryOptions
  });

  let filteredTabs = tabs.filter(tab => !tab.showTab || (tab?.showTab && tab?.showTab(queryResult?.data)));

  return (
    <PageDataContext.Provider value={queryResult}>
    <Container>
      {show_breadcrumbs ? <Breadcrumbs aria-label="breadcrumb" sx={{ marginBottom: '1rem' }}>
        <StyledLink to={baseUrl}>{title}s</StyledLink>
        <StyledLink to={pathname}>View {title}</StyledLink>
        <StyledLink to="#" $active={"true"}>{queryResult?.data?.[identifier]}</StyledLink>
      </Breadcrumbs> : null}
        {isProfile ? <Grid container spacing={2}>
        <Grid size={12}>
          <ProfileBlock>
            <StyledAvatar sx={{ bgcolor: theme.palette.primary.main }}>
              <PhotoCameraFrontRoundedIcon sx={{ fontSize: "6rem"}} />
            </StyledAvatar>
          </ProfileBlock>
        </Grid>
        </Grid> : null }
          
       {queryResult.isLoading ? <LoadingPageComponent /> : <>
          {filteredTabs?.length ? <Tabs value={activeTab} aria-label="basic tabs example" onChange={handleChangeTab} sx={{borderBottom: "1px solid #DEDEDF", minHeight: "auto"}} variant="scrollable">
            {filteredTabs.map((tab, index) => (
              <StyledTab key={tab.label} label={<TabLabel label={tab.label} badge={tab.badge} />} icon={tab?.icon} iconPosition={isMobile ? "top" : "start"} value={index} />
            ))}
          </Tabs> : null}
          {filteredTabs?.length && tabContent?.length ? tabContent.map((tab, index) => (
            <TabContent key={tab.panel} panel={index} value={activeTab} >{tab.content}</TabContent>)
          ) : null}

          {!filteredTabs?.length ? children : null}
        </> }
    </Container>
    </PageDataContext.Provider>
  )
};

export default ViewDetails;